import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { Wallet, ArrowDownRight, ArrowUpRight, Clock, Landmark, CheckCircle, ShieldCheck, ChevronRight, Activity, TrendingUp, Edit2, Info, Navigation, Map, Inbox } from 'lucide-react';
import axios from 'axios';
import './Wallet.css';
import Swal from 'sweetalert2';
import api from '../api';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { load } from '@cashfreepayments/cashfree-js';

export default function WalletPage() {
  const navigate = useNavigate();
  const { seller } = useSelector((s: RootState) => s.auth);

  const [walletData, setWalletData] = useState<any>(null);
  const [txList, setTxList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalEarnedMonth, setTotalEarnedMonth] = useState(0);
  const [negativeBalanceLimit, setNegativeBalanceLimit] = useState(0);

  // Pagination & Filters
  const [txPage, setTxPage] = useState(1);
  const [txLimit, setTxLimit] = useState(10);
  const [txType, setTxType] = useState('ALL');
  const [totalPages, setTotalPages] = useState(1);

  // Transaction details modal
  const [selectedTx, setSelectedTx] = useState<any>(null);

  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [selectedBank, setSelectedBank] = useState(false);

  const [showTopupModal, setShowTopupModal] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');

  const [withdrawalHistory, setWithdrawalHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [showBankModal, setShowBankModal] = useState(false);
  const [accountHolderName, setAccountHolderName] = useState(seller?.bankdetails?.account_holder_name || '');
  const [ifsc, setIfsc] = useState(seller?.bankdetails?.ifsc_code || '');
  const [bankName, setBankName] = useState(seller?.bankdetails?.bank_name || '');
  const [branchName, setBranchName] = useState('');
  const [city, setCity] = useState('');
  const [stateName, setStateName] = useState('');
  const [accountNumber, setAccountNumber] = useState(seller?.bankdetails?.account_number || '');
  const [email, setEmail] = useState(seller?.email || '');
  const [phone, setPhone] = useState(seller?.mobile || seller?.phone || '');
  const [isLoadingIfsc, setIsLoadingIfsc] = useState(false);
  const [ifscError, setIfscError] = useState('');

  useEffect(() => {
    if (seller) {
      setEmail(prev => prev || seller.email || '');
      setPhone(prev => prev || seller.mobile || seller.phone || '');

      if (seller.bankdetails) {
        setAccountHolderName(prev => prev || seller.bankdetails.account_holder_name || '');
        setAccountNumber(prev => prev || seller.bankdetails.account_number || '');
        setIfsc(prev => prev || seller.bankdetails.ifsc_code || '');
        setBankName(prev => prev || seller.bankdetails.bank_name || '');
      }
    }
  }, [seller]);

  useEffect(() => {
    if (showWithdrawModal && seller?._id) {
      setLoadingHistory(true);
      api.get(`/seller-wallet/transactions/${seller._id}`)
        .then(res => {
          if (res.data.success) {
            setWithdrawalHistory(res.data.data);
          }
        })
        .catch(err => console.error("Error fetching history", err))
        .finally(() => setLoadingHistory(false));
    }
  }, [showWithdrawModal, seller?._id]);

  useEffect(() => {
    const cleanIfsc = ifsc.trim().toUpperCase();
    if (cleanIfsc.length === 11) {
      setIsLoadingIfsc(true);
      setIfscError('');
      axios.get(`https://ifsc.razorpay.com/${cleanIfsc}`)
        .then(res => {
          setBankName(res.data.BANK || '');
          setBranchName(res.data.BRANCH || '');
          setCity(res.data.CITY || '');
          setStateName(res.data.STATE || '');
        })
        .catch(err => {
          setBankName('');
          setBranchName('');
          setCity('');
          setStateName('');
          setIfscError('Invalid IFSC Code. Please check and try again.');
        })
        .finally(() => setIsLoadingIfsc(false));
    } else {
      setBankName('');
      setBranchName('');
      setCity('');
      setStateName('');
      setIfscError('');
    }
  }, [ifsc]);

  const handleBankSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !accountHolderName.trim() ||
      !accountNumber.trim() ||
      !ifsc.trim() ||
      !bankName.trim() ||
      !branchName.trim() ||
      !city.trim() ||
      !stateName.trim()
    ) {
      Swal.fire("Error", "Please fill out all required fields properly.", "error");
      return;
    }

    try {
      const payload = {
        userId: seller?._id,
        paymentMode: "Bank Transfer",
        accountHolderName,
        bankName,
        branchName,
        city,
        state: stateName,
        ifsc,
        accountNumber,
        email,
        phone
      };

      const res = await api.post(`/withdrawal-update-bankdetails`, payload);
      if (res.data.success || res.data.message) {
        Swal.fire("Success!", "Bank details saved successfully!", "success");
        setShowBankModal(false);
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Error saving bank details", "error");
    }
  };

  const handleWithdrawClick = () => {
    const bd = seller?.bankdetails;
    if (!bd?.account_number || !bd?.bank_name || !bd?.ifsc_code) {
      setShowBankModal(true);
    } else {
      setShowWithdrawModal(true);
    }
  };

  const handleCloseWithdrawModal = () => {
    setShowWithdrawModal(false);
    setWithdrawAmount('');
    setSelectedBank(false);
  };

  useEffect(() => {
    if (showWithdrawModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showWithdrawModal]);

  const fetchWalletData = () => {
    if (seller?._id) {
      setLoading(true);
      api.get(`/seller-wallet/${seller._id}?page=${txPage}&limit=${txLimit}&type=${txType}`)
        .then(res => {
          if (res.data.success) {
            setWalletData(res.data.data.wallet);
            setTxList(res.data.data.transactions);
            setTotalEarnedMonth(res.data.data.totalEarnedMonth || 0);
            setTotalPages(res.data.data.totalPages || 1);
            if (res.data.data.negativeBalanceLimit !== undefined) {
              setNegativeBalanceLimit(res.data.data.negativeBalanceLimit);
            }
          }
        })
        .catch(err => console.error('Error fetching wallet:', err))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWalletData();
  }, [seller, txPage, txLimit, txType]);

  const formatDate = (date: any) => new Date(date || Date.now()).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  const balance = walletData?.availableBalance || 0;
  const pendingBalance = walletData?.pendingBalance || 0;

  const formattedTransactions = txList.slice(0, 3).map((t: any) => {
    let comm = t.commissionDeducted || 0;
    let gst = t.gstDeducted || 0;
    let tcs = 0;
    let platformFee = 0;
    let sms = 0;
    let pgFee = 0;
    let totalDeduction = t.commission_GST_Deducted || 0;
    let desc = t.description || 'Transaction';

    if (desc && (desc.includes('[Comm:') || desc.includes('(Comm:'))) {
      const commMatch = desc.match(/Comm:\s*₹?(\d+(\.\d+)?)/);
      const gstMatch = desc.match(/GST:\s*₹?(\d+(\.\d+)?)/);
      const tcsMatch = desc.match(/TCS:\s*₹?(\d+(\.\d+)?)/);
      const platformFeeMatch = desc.match(/Platform Fee:\s*₹?(\d+(\.\d+)?)/);
      const smsMatch = desc.match(/SMS:\s*₹?(\d+(\.\d+)?)/);
      const pgFeeMatch = desc.match(/PG Fee:\s*₹?(\d+(\.\d+)?)/);

      if (commMatch && comm === 0) comm = parseFloat(commMatch[1]);
      if (gstMatch && gst === 0) gst = parseFloat(gstMatch[1]);
      if (tcsMatch) tcs = parseFloat(tcsMatch[1]);
      if (platformFeeMatch) platformFee = parseFloat(platformFeeMatch[1]);
      if (smsMatch) sms = parseFloat(smsMatch[1]);
      if (pgFeeMatch) pgFee = parseFloat(pgFeeMatch[1]);

      if (totalDeduction === 0) {
        totalDeduction = comm + gst + tcs + platformFee + sms + pgFee;
      }
    }

    let cleanDesc = desc.replace(/\s*[\[\(]Comm:.*?[\]\)]/i, '').trim();
    if (cleanDesc.includes('Wallet Topup')) {
      cleanDesc = 'Wallet Topup';
    }

    return {
      id: t._id,
      ref: cleanDesc,
      date: t.createdAt,
      amount: t.amount || 0,
      type: (t.transactionType || 'credit').toLowerCase(),
      status: (t.status || 'completed').toLowerCase(),
      orderId: t.orderId,
      orderTransactionId: t.orderTransactionId,
      productId: t.productId,
      commissionDeducted: comm,
      commission_GST_Deducted: totalDeduction,
      gstDeducted: gst,
      tcsDeducted: tcs,
      platformFee: platformFee,
      smsCharges: sms,
      pgFee: pgFee
    };
  });

  const getTxStyles = (tx: any) => {
    const status = (tx.status || 'pending').toLowerCase();
    const type = (tx.type || 'credit').toLowerCase();
    let amountClass = 'tx-amount-success';
    let statusClass = 'tx-status-completed';
    let sign = type === 'credit' ? '+' : '-';
    let Icon = type === 'credit' ? ArrowDownRight : ArrowUpRight;
    let iconWrapClass = type === 'credit' ? 'tx-credit-icon' : 'tx-debit-icon';

    let badgeText = 'CREDIT';

    if (status === 'pending' || status === 'processing') {
      amountClass = 'tx-amount-neutral';
      statusClass = 'tx-status-pending';
      badgeText = 'PROCESSING';
    } else if (status === 'failed') {
      amountClass = 'tx-amount-failed';
      statusClass = 'tx-status-failed';
      badgeText = 'FAILED';
    } else if (status === 'refunded') {
      amountClass = 'tx-amount-refunded';
      statusClass = 'tx-status-refunded';
      badgeText = 'REFUNDED';
    } else if (type === 'debit') {
      amountClass = 'tx-amount-failed';
      statusClass = 'tx-status-failed';
      badgeText = 'DEBIT';
    }

    return { amountClass, statusClass, sign, Icon, iconWrapClass, badgeText };
  };

  const handleWithdraw = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBank) {
      Swal.fire("Error", "Please select a destination bank account.", "error");
      return;
    }

    try {
      const payload = {
        data: {
          userId: seller?._id,
          amount: Number(withdrawAmount),
          source: 'DecsoCart'
        }
      };

      const res = await api.post(`/v1/payout/withdraw`, payload);
      if (res.data.status === 'PROCESSING' || res.data.status === 'INITIATED') {
        Swal.fire("Success!", "Withdrawal request submitted successfully!", "success");
        handleCloseWithdrawModal();

        // Trigger a re-fetch of wallet data
        if (seller?._id) {
          api.get(`/seller-wallet/${seller._id}`)
            .then(walletRes => {
              if (walletRes.data.success) {
                setWalletData(walletRes.data.data.wallet);
                setTxList(walletRes.data.data.transactions);
                window.dispatchEvent(new Event('walletUpdated'));
              }
            });
        }
      }
    } catch (err: any) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || "Error processing withdrawal", "error");
    }
  };

  const handleTopupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!topupAmount || Number(topupAmount) < 100) {
      return Swal.fire("Invalid Amount", "Amount must be at least ₹100", "error");
    }
    if (Number(topupAmount) > 100000) {
      return Swal.fire("Invalid Amount", "Amount cannot exceed ₹1,00,000", "error");
    }

    try {
      const payload = {
        amount: Number(topupAmount),
        sellerId: seller?._id,
        customerName: seller?.storeinfo?.store_name || seller?.name || "Seller",
        customerPhone: seller?.mobile || seller?.phone || "9999999999",
        customerEmail: seller?.email || "seller@example.com"
      };
      const res = await api.post(`/seller-wallet/topup/create-order`, payload);

      if (res.data.success && res.data.order) {
        const cashfreeEnv = res.data.env === 'production' ? 'production' : 'sandbox';
        const cashfree = await load({ mode: cashfreeEnv as any });
        
        if (!cashfree) return Swal.fire("Error", "Failed to load payment gateway", "error");

        const checkoutOptions = {
          paymentSessionId: res.data.order.payment_session_id,
          redirectTarget: "_modal"
        };

        cashfree.checkout(checkoutOptions).then((result: any) => {
          if (result.error) {
            Swal.fire("Error", result.error.message, "error");
          }
          if (result.paymentDetails) {
            api.post(`/v1/seller-wallet/topup/verify`, {
              orderId: res.data.order.order_id,
              sellerId: seller?._id,
              amount: Number(topupAmount)
            }).catch(() => api.post(`/seller-wallet/topup/verify`, {
              orderId: res.data.order.order_id,
              sellerId: seller?._id,
              amount: Number(topupAmount)
            })).then(verifyRes => {
              if (verifyRes.data.success) {
                Swal.fire("Success!", "Wallet topped up successfully!", "success");
                setShowTopupModal(false);
                setTopupAmount('');
                if (seller?._id) {
                  api.get(`/seller-wallet/${seller._id}`).then(walletRes => {
                    if (walletRes.data.success) {
                      setWalletData(walletRes.data.data.wallet);
                      setTxList(walletRes.data.data.transactions);
                      window.dispatchEvent(new Event('walletUpdated'));
                    }
                  });
                }
              } else {
                Swal.fire("Error", "Payment verification failed", "error");
              }
            });
          }
        });
      }
    } catch (err: any) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || "Error initiating topup", "error");
    }
  };

  const showReceiptModal = (productName: string, productAmount: number, adminCommission: number, gstOnComm: number, tcs: number, platformFee: number, sms: number, pgFee: number, netSettlement: number, txDate: string, orderId: string, txType: string = 'credit') => {
    const isDebit = txType.toLowerCase() === 'debit';
    const totalFees = adminCommission + gstOnComm + tcs + platformFee + sms + pgFee;
    const grossTotal = netSettlement + totalFees;
    const receiptHtml = `
      <div style="text-align: left; font-family: monospace; background: #fff; padding: 20px; border-radius: 8px; border: 1px dashed #cbd5e1; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);">
        <div style="text-align: center; border-bottom: 2px dashed #94a3b8; padding-bottom: 10px; margin-bottom: 20px;">
          <h2 style="margin: 0; font-size: 20px; color: #0f172a;">SETTLEMENT RECEIPT</h2>
          <p style="margin: 5px 0 0; font-size: 12px; color: #64748b;">${txDate}</p>
          ${orderId ? `<p style="margin: 3px 0 0; font-size: 11px; color: #64748b; font-weight: 600;">Order #${orderId}</p>` : ''}
        </div>
        
        <div style="margin-bottom: 15px; font-size: 14px; color: #0f172a; font-weight: bold; text-align: center;">
          ${productName}
        </div>

        ${!isDebit ? `
        <div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px;">
          <span style="color: #475569;">Gross Amount</span>
          <span style="font-weight: 600;">₹${grossTotal.toLocaleString('en-IN')}</span>
        </div>` : ''}
        ${adminCommission > 0 ? `<div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px; color: #ef4444;">
          <span>Admin Commission</span>
          <span>-₹${adminCommission.toLocaleString('en-IN')}</span>
        </div>` : ''}
        ${gstOnComm > 0 ? `<div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px; color: #ef4444;">
          <span>GST on Comm</span>
          <span>-₹${gstOnComm.toLocaleString('en-IN')}</span>
        </div>` : ''}
        ${tcs > 0 ? `<div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px; color: #ef4444;"><span>TCS</span><span>-₹${tcs.toLocaleString('en-IN')}</span></div>` : ''}
        ${platformFee > 0 ? `<div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px; color: #ef4444;"><span>Platform Fee</span><span>-₹${platformFee.toLocaleString('en-IN')}</span></div>` : ''}
        ${sms > 0 ? `<div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px; color: #ef4444;"><span>SMS Charges</span><span>-₹${sms.toLocaleString('en-IN')}</span></div>` : ''}
        ${pgFee > 0 ? `<div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px; color: #ef4444;"><span>PG Fee</span><span>-₹${pgFee.toLocaleString('en-IN')}</span></div>` : ''}
        
        <div style="border-top: 2px dashed #94a3b8; padding-top: 15px; display: flex; justify-content: space-between; font-size: 16px; font-weight: 700; color: ${isDebit ? '#ef4444' : '#0f766e'};">
          <span>${isDebit ? 'Total Deducted' : 'Net Settlement'}</span>
          <span>${isDebit ? '-' : ''}₹${(isDebit ? totalFees : netSettlement).toLocaleString('en-IN')}</span>
        </div>
        
        <div style="text-align: center; margin-top: 25px; font-size: 11px; color: #94a3b8;">
          Thank you for selling with DecsoPlus!
        </div>
      </div>
    `;

    Swal.fire({
      html: receiptHtml,
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: 'Close',
      cancelButtonText: 'Download PDF',
      confirmButtonColor: '#94a3b8',
      cancelButtonColor: '#6366f1',
      width: '400px',
      reverseButtons: true
    }).then((result) => {
      if (result.dismiss === Swal.DismissReason.cancel) {
        // Generate PDF
        const doc = new jsPDF();
        doc.setFont("Helvetica", "bold");
        doc.setFontSize(20);
        doc.setTextColor(15, 118, 110); // Teal
        doc.text("SETTLEMENT RECEIPT", 105, 20, { align: "center" });

        doc.setFontSize(10);
        doc.setFont("Helvetica", "normal");
        doc.setTextColor(100, 116, 139);
        doc.text(txDate, 105, 28, { align: "center" });
        if (orderId) doc.text(`Order #${orderId}`, 105, 34, { align: "center" });

        doc.setFontSize(14);
        doc.setFont("Helvetica", "bold");
        doc.setTextColor(15, 23, 42);
        const splitTitle = doc.splitTextToSize(productName, 170);
        doc.text(splitTitle, 105, 48, { align: "center" });

        const tableBody: any[] = [];
        if (!isDebit) {
          tableBody.push(['Gross Amount', `Rs. ${grossTotal.toLocaleString('en-IN')}`]);
        }
        if (adminCommission > 0) tableBody.push(['Admin Commission', `-Rs. ${adminCommission.toLocaleString('en-IN')}`]);
        if (gstOnComm > 0) tableBody.push(['GST on Commission', `-Rs. ${gstOnComm.toLocaleString('en-IN')}`]);
        if (tcs > 0) tableBody.push(['TCS', `-Rs. ${tcs.toLocaleString('en-IN')}`]);
        if (platformFee > 0) tableBody.push(['Platform Fee', `-Rs. ${platformFee.toLocaleString('en-IN')}`]);
        if (sms > 0) tableBody.push(['SMS Charges', `-Rs. ${sms.toLocaleString('en-IN')}`]);
        if (pgFee > 0) tableBody.push(['PG Fee', `-Rs. ${pgFee.toLocaleString('en-IN')}`]);

        autoTable(doc, {
          startY: 60,
          theme: 'grid',
          headStyles: { fillColor: [248, 250, 252], textColor: [15, 23, 42] },
          body: tableBody,
        });

        doc.setFontSize(14);
        doc.setFont("Helvetica", "bold");
        if (isDebit) {
          doc.setTextColor(239, 68, 68);
          doc.text(`Total Deducted: -Rs. ${totalFees.toLocaleString('en-IN')}`, 14, (doc as any).lastAutoTable.finalY + 15);
        } else {
          doc.setTextColor(15, 118, 110);
          doc.text(`Net Settlement: Rs. ${netSettlement.toLocaleString('en-IN')}`, 14, (doc as any).lastAutoTable.finalY + 15);
        }

        doc.save(`Receipt_${orderId || Date.now()}.pdf`);
      }
    });
  };

  return (
    <div className="wallet-page">
      <div className="wallet-container fade-in-stagger">
        <div className="wallet-header slide-up-item">
          <div>
            <h1 className="wallet-title">Finances</h1>
            <p className="wallet-subtitle">Track your earnings and process withdrawals instantly.</p>
          </div>
          <div className="system-status">
            <div className="status-dot" />
            <span className="status-text">All systems operational</span>
          </div>
        </div>

        {/* Dashboard Grid */}
        <div className="wallet-grid">

          {/* Main Balance Card */}
          <div className="balance-card slide-up-item">
            {/* Animated Glow Elements */}
            <div className="glow-teal" />
            <div className="glow-indigo" />

            <div className="balance-content">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                <div>
                  <div className="balance-header">
                    <Wallet size={20} color="#2dd4bf" />
                    <span className="balance-label">Available Balance</span>
                  </div>
                  <h2 className="balance-amount">
                    ₹{balance.toLocaleString('en-IN')}
                  </h2>
                </div>

                <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                    <ShieldCheck size={18} color="#2dd4bf" />
                    <span style={{ color: '#94a3b8', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Floating Cash Limit</span>
                  </div>
                  <h3 style={{ margin: 0, color: '#fff', fontSize: '28px', fontWeight: '800', lineHeight: 1.1 }}>
                    ₹{negativeBalanceLimit.toLocaleString('en-IN')}
                  </h3>
                  <div style={{ background: 'rgba(16, 185, 129, 0.2)', color: '#34d399', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <CheckCircle size={12} /> ACTIVE
                  </div>
                </div>
              </div>

              <div className="balance-actions">
                <button
                  onClick={() => setShowTopupModal(true)}
                  className="btn-withdraw-main"
                  style={{ background: 'rgba(255, 255, 255, 0.2)', marginRight: '10px' }}
                >
                  <ArrowDownRight size={22} />
                  <span style={{ color: "#fff" }}>Topup</span>
                </button>
                <button
                  onClick={handleWithdrawClick}
                  className="btn-withdraw-main"
                >
                  <ArrowUpRight size={22} />
                  <span>Withdraw</span>
                </button>
              </div>
            </div>
          </div>

          {/* Stats Column */}
          <div className="stats-column slide-up-item" style={{ animationDelay: '0.1s' }}>
            <div className="stat-card">
              <div className="stat-icon-bg">
                <TrendingUp size={140} strokeWidth={1} />
              </div>
              <p className="stat-label">Total Earned (Month)</p>
              <h3 className="stat-value">₹{totalEarnedMonth.toLocaleString('en-IN')}</h3>
              <div className="stat-badge">
                <ArrowUpRight size={16} /> +12.5%
              </div>
            </div>

          </div>
        </div>

        {/* Transaction History */}
        <div className="tx-container slide-up-item" style={{ animationDelay: '0.2s' }}>
          <div className="tx-header">
            <h3 className="tx-title">
              <div className="tx-icon-wrapper">
                <Clock size={24} />
              </div>
              Recent Activity
            </h3>
            <button className="tx-view-all" onClick={() => navigate('/wallet/activity')} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'transparent', border: 'none', color: '#3b82f6', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}>
              View All <ChevronRight size={18} />
            </button>
          </div>

          <div className="tx-list" style={{ marginTop: '16px' }}>
            {loading ? (
              <p style={{ textAlign: 'center', padding: '40px 20px', color: '#888' }}>
                <div style={{ width: '24px', height: '24px', border: '3px solid #f3f3f3', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
                Loading transactions...
              </p>
            ) : formattedTransactions.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '40px 20px', color: '#4d4b4bff', background: '#f8fafc', borderRadius: '12px', height: "36vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                <div style={{ background: '#f3f2f2ff', padding: '16px', borderRadius: '50%' }}>

                  <Inbox size={40} color="#808386ff" style={{ display: 'block'  }} />
                </div>
                No transactions found.
              </p>
            ) : formattedTransactions.map((tx: any, idx: number) => {
              const { amountClass, statusClass, sign, Icon, iconWrapClass, badgeText } = getTxStyles(tx);
              return (
                <div
                  key={tx.id}
                  className="tx-item slide-up-item"
                  style={{ animationDelay: `${0.1 + (idx * 0.05)}s`, cursor: 'pointer' }}
                  onClick={() => setSelectedTx(tx)}
                >
                  <div className="tx-col tx-col-primary">
                    <div className={`tx-icon ${iconWrapClass}`}>
                      <Icon size={20} />
                    </div>
                    <div className="tx-title-group">
                      <h4 className="tx-ref">{tx.ref}</h4>
                      <p className="tx-subtitle">
                        {tx.orderId ? `Order #${tx.orderId} • ` : `Txn ID: ${tx.id.slice(-8).toUpperCase()} • `}{formatDate(tx.date)}
                      </p>
                    </div>
                  </div>

                  <div className="tx-col tx-col-amount">
                    <p className={`tx-amount ${amountClass}`}>
                      {sign}₹{tx.amount.toLocaleString('en-IN')}
                    </p>
                    <span className={`tx-status-badge ${statusClass}`}>
                      {badgeText}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Transaction Details Modal */}
      {selectedTx && createPortal(
        <div className="modal-overlay fade-in" onClick={() => setSelectedTx(null)}>
          <div className="modal-content scale-up" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px', padding: 0, borderRadius: '16px', overflow: 'hidden' }}>
            <div className="modal-header" style={{ padding: '24px 24px 20px', margin: 0, borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
              <div className="modal-title-wrap">
                <div className="modal-icon"><Info size={24} color="#3b82f6" /></div>
                <h3 className="modal-title" style={{ margin: 0 }}>Transaction Details</h3>
              </div>
              <button type="button" onClick={() => setSelectedTx(null)} className="modal-close" style={{ top: '24px', right: '24px' }}>&times;</button>
            </div>
            <div className="modal-body" style={{ padding: '24px', margin: 0, background: '#f8fafc' }}>

              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>Amount</p>
                <h2 style={{ margin: 0, fontSize: '32px', color: selectedTx.type === 'credit' ? '#10b981' : '#ef4444' }}>
                  {selectedTx.type === 'credit' ? '+' : '-'}₹{selectedTx.amount?.toLocaleString('en-IN')}
                </h2>
                <span style={{ display: 'inline-block', marginTop: '8px', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', background: selectedTx.status === 'COMPLETED' || selectedTx.status === 'PAID' ? '#d1fae5' : selectedTx.status === 'FAILED' ? '#fee2e2' : '#fef3c7', color: selectedTx.status === 'COMPLETED' || selectedTx.status === 'PAID' ? '#059669' : selectedTx.status === 'FAILED' ? '#dc2626' : '#d97706' }}>
                  {selectedTx.status}
                </span>
              </div>

              <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
                  <span style={{ color: '#64748b', fontSize: '14px' }}>Date & Time</span>
                  <span style={{ color: '#0f172a', fontSize: '14px', fontWeight: '500' }}>{formatDate(selectedTx.date)}</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
                  <span style={{ color: '#64748b', fontSize: '14px' }}>Reference</span>
                  <span style={{ color: '#0f172a', fontSize: '14px', fontWeight: '500', maxWidth: '60%', textAlign: 'right', wordBreak: 'break-word' }}>{selectedTx.ref}</span>
                </div>

                {selectedTx.orderId && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
                    <span style={{ color: '#64748b', fontSize: '14px' }}>Order ID</span>
                    <span style={{ color: '#0f172a', fontSize: '14px', fontWeight: '500' }}>{selectedTx.orderId}</span>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b', fontSize: '14px', whiteSpace: 'nowrap' }}>Transaction ID</span>
                  <span 
                    title={selectedTx.ref && selectedTx.ref.includes('COD Order') ? '-' : (selectedTx.orderTransactionId || selectedTx.id)}
                    style={{ color: '#0f172a', fontSize: '14px', fontWeight: '500', fontFamily: 'monospace', cursor: 'help', textAlign: 'right', wordBreak: 'break-all', marginLeft: '16px' }}
                  >
                    {(() => {
                      const idStr = String(selectedTx.ref && selectedTx.ref.includes('COD Order') ? '-' : (selectedTx.orderTransactionId || selectedTx.id));
                      return idStr.length > 20 ? idStr.substring(0, 20) + '...' : idStr;
                    })()}
                  </span>
                </div>
              </div>

            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Bank Details Modal */}
      {showBankModal && createPortal(
        <div className="modal-overlay fade-in" onClick={() => setShowBankModal(false)}>
          <div className="modal-content scale-up" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', padding: 0, borderRadius: '16px', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
            <div className="modal-header" style={{ padding: '24px 24px 20px', margin: 0, borderBottom: '1px solid #e2e8f0', background: '#fff', zIndex: 10 }}>
              <div className="modal-title-wrap">
                <div className="modal-icon"><Landmark size={24} /></div>
                <h3 className="modal-title" style={{ margin: 0 }}>Payment Details Setup</h3>
              </div>
              <button type="button" onClick={() => setShowBankModal(false)} className="modal-close" style={{ top: '24px', right: '24px' }}>&times;</button>
            </div>

            <form onSubmit={handleBankSubmit} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, margin: 0 }}>
              <div className="modal-body" style={{ overflowY: 'auto', padding: '24px', margin: 0, flex: 1 }}>
                <div className="form-group">
                  <label className="form-label">Account Holder Name</label>
                  <input required className="form-input" style={{ paddingLeft: '16px' }} value={accountHolderName} onChange={e => setAccountHolderName(e.target.value)} />
                </div>

                <div className="form-group">
                  <label className="form-label">Account Number</label>
                  <input required type="text" className="form-input" style={{ paddingLeft: '16px' }} value={accountNumber} onChange={e => setAccountNumber(e.target.value)} />
                </div>
                <h4 style={{ fontSize: '16px', fontWeight: '700', color: '#334155', marginTop: '24px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  Bank Information
                </h4>

                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Info size={16} color="#475569" /> IFSC Code
                  </label>
                  <input required type="text" className="form-input" style={{ paddingLeft: '16px', textTransform: 'uppercase', background: '#f8fafc', borderColor: ifscError ? '#ef4444' : undefined }} placeholder="e.g., SBIN0001234" maxLength={11} value={ifsc} onChange={e => setIfsc(e.target.value)} />
                  {isLoadingIfsc && <div style={{ fontSize: '12px', color: '#14b8a6', marginTop: '6px' }}>Fetching bank details...</div>}
                  {ifscError && <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '6px', fontWeight: '500' }}>{ifscError}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">Bank Name</label>
                  <div className="input-wrap" style={{ position: 'relative' }}>
                    <Landmark size={18} color="#94a3b8" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)' }} />
                    <input readOnly type="text" className="form-input" style={{ paddingLeft: '44px', background: '#f8fafc' }} placeholder="Bank Name" value={bankName} onChange={e => setBankName(e.target.value)} />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Branch Name</label>
                  <div className="input-wrap" style={{ position: 'relative' }}>
                    <Navigation size={18} color="#94a3b8" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)' }} />
                    <input readOnly type="text" className="form-input" style={{ paddingLeft: '44px', background: '#f8fafc' }} placeholder="Branch Name" value={branchName} onChange={e => setBranchName(e.target.value)} />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label className="form-label">City</label>
                    <div className="input-wrap" style={{ position: 'relative' }}>
                      <Map size={18} color="#94a3b8" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)' }} />
                      <input readOnly type="text" className="form-input" style={{ paddingLeft: '44px', background: '#f8fafc' }} placeholder="City" value={city} onChange={e => setCity(e.target.value)} />
                    </div>
                  </div>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label className="form-label">State</label>
                    <div className="input-wrap" style={{ position: 'relative' }}>
                      <Map size={18} color="#94a3b8" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)' }} />
                      <input readOnly type="text" className="form-input" style={{ paddingLeft: '44px', background: '#f8fafc' }} placeholder="State" value={stateName} onChange={e => setStateName(e.target.value)} />
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '16px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">Email</label>
                    <input type="email" required className="form-input" style={{ paddingLeft: '16px' }} value={email} onChange={e => setEmail(e.target.value)} />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">Phone</label>
                    <input type="text" required className="form-input" style={{ paddingLeft: '16px' }} value={phone} onChange={e => setPhone(e.target.value)} />
                  </div>
                </div>

              </div>
              <div className="modal-footer" style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', background: '#fff', margin: 0, display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowBankModal(false)} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#475569', fontWeight: '600', cursor: 'pointer', fontSize: '14px', transition: 'all 0.2s ease' }}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" style={{ margin: 0, width: 'auto', padding: '10px 24px', borderRadius: '8px', fontSize: '14px' }}>
                  Save Details
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Withdraw Modal */}
      {showWithdrawModal && createPortal(
        <div className="modal-overlay fade-in" onClick={() => setShowWithdrawModal(false)}>
          <div className={`modal-content ${withdrawalHistory.length > 0 ? 'fullscreen-modal' : ''} scale-up`} onClick={(e) => e.stopPropagation()}>
            <div className={withdrawalHistory.length > 0 ? "modal-split-layout" : ""}>
              {/* LEFT PANEL: Form */}
              <div className={withdrawalHistory.length > 0 ? "modal-left" : "modal-body"}>
                <div className="modal-header">
                  <div className="modal-title-wrap">
                    <div className="modal-icon">
                      <Landmark size={24} />
                    </div>
                    <h3 className="modal-title">Withdraw</h3>
                  </div>
                  <button onClick={() => setShowWithdrawModal(false)} className="modal-close" style={{ display: withdrawalHistory.length > 0 ? 'none' : 'flex' }}>
                    &times;
                  </button>
                </div>

                <div className="modal-balance">
                  <span className="modal-balance-label">Available to Withdraw</span>
                  <span className="modal-balance-amount">₹{balance.toLocaleString('en-IN')}</span>
                </div>

                <form onSubmit={handleWithdraw}>
                  <div className="form-group">
                    <label className="form-label">Amount (₹)</label>
                    <div className="input-wrap">
                      <input
                        type="number"
                        required
                        min="1"
                        max={balance > 0 ? balance : 1}
                        value={withdrawAmount}
                        onChange={(e) => setWithdrawAmount(e.target.value)}
                        className="form-input"
                        placeholder="0"
                        style={{ paddingLeft: '16px' }}
                      />
                    </div>
                    {balance === 0 ? (
                      <div className="error-msg fade-in">
                        <ShieldCheck size={18} /> Insufficient balance to withdraw.
                      </div>
                    ) : withdrawAmount && Number(withdrawAmount) > balance ? (
                      <div className="error-msg fade-in" style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <ShieldCheck size={14} /> Amount exceeds available balance.
                      </div>
                    ) : withdrawAmount && Number(withdrawAmount) < 1 ? (
                      <div className="error-msg fade-in" style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <ShieldCheck size={14} /> Minimum withdrawal amount is ₹1.
                      </div>
                    ) : null}
                  </div>

                  <div className="form-group">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <label className="form-label" style={{ margin: 0 }}>Destination</label>
                      <div
                        onClick={() => {
                          handleCloseWithdrawModal();
                          setShowBankModal(true);
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', color: '#14b8a6', fontSize: '13px', fontWeight: 'bold' }}
                      >
                        <Edit2 size={14} /> Edit
                      </div>
                    </div>
                    <div
                      onClick={() => setSelectedBank(!selectedBank)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        border: selectedBank ? '1.5px solid #14b8a6' : '1.5px solid #e2e8f0',
                        borderRadius: '16px',
                        cursor: 'pointer',
                        background: selectedBank ? '#f0fdf4' : '#f8fafc',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ width: '40px', height: '40px', background: '#fff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                        <Landmark size={20} color="#0f766e" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '15px', fontWeight: '700', color: '#0f766e', marginBottom: '2px' }}>{seller?.bankdetails?.bank_name || 'Bank Account Not Set'}</div>
                        <div style={{ fontSize: '13px', color: '#0f766e', fontWeight: '500' }}>Ends in XXXX{seller?.bankdetails?.account_number ? String(seller.bankdetails.account_number).slice(-4) : 'XXXX'}</div>
                      </div>
                      {selectedBank ? (
                        <CheckCircle size={22} fill="#14b8a6" color="#fff" />
                      ) : (
                        <div style={{ width: '22px', height: '22px', borderRadius: '50%', border: '2px solid #cbd5e1' }}></div>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={Number(withdrawAmount) > balance || Number(withdrawAmount) < 1 || !selectedBank}
                    className="btn-submit"
                  >
                    Confirm Withdrawal
                  </button>
                </form>
              </div>

              {/* RIGHT PANEL: History */}
              {withdrawalHistory.length > 0 && (
                <div className="modal-right">
                  <div className="modal-header" style={{ marginBottom: '24px' }}>
                    <h3 className="modal-title" style={{ fontSize: '20px' }}>Transaction History</h3>
                    <button onClick={() => setShowWithdrawModal(false)} className="modal-close" style={{ margin: 0 }}>
                      &times;
                    </button>
                  </div>

                  {loadingHistory ? (
                    <div style={{ color: '#64748b', fontSize: '14px', textAlign: 'center', padding: '40px 0' }}>Loading history...</div>
                  ) : (
                    <div className="history-list">
                      {withdrawalHistory.map(tx => (
                        <div className="history-card fade-in" key={tx._id} style={{ display: 'flex', flexDirection: 'column' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                            <div className="history-left-group">
                              <div className={`history-icon-bg bg-${tx.transactionType}`}>
                                {tx.transactionType === 'CREDIT' ? <ArrowDownRight size={22} /> : <ArrowUpRight size={22} />}
                              </div>
                              <div className="history-info">
                                <div className="history-title-row">
                                  <span className="history-title">{(tx.description || '').replace(/\s*[\[\(]Comm:.*?[\]\)]/i, '').trim() || (tx.orderId ? `Order #${tx.orderId}` : 'Transaction')}</span>
                                </div>
                                <span className="history-date">{new Date(tx.createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</span>
                              </div>
                            </div>
                            <div className="history-amounts">
                              <span className={`history-amount-main txType-${tx.transactionType}`}>
                                {tx.transactionType === 'CREDIT' ? '+' : '-'}₹{tx.amount?.toLocaleString('en-IN') || 0}
                              </span>
                              <div className="history-meta-row">
                                {tx.totalAmount && tx.transactionType === 'DEBIT' && (
                                  <span className="history-total-amount">Sent: ₹{tx.totalAmount}</span>
                                )}
                                <span className={`history-status-badge status-${tx.status || 'completed'}`}>{tx.status || 'Completed'}</span>
                              </div>
                            </div>
                          </div>
                          {(tx.commissionDeducted > 0 || tx.commission_GST_Deducted > 0) && (
                            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                              <button
                                type="button"
                                style={{ padding: '6px 12px', fontSize: '12px', background: '#f8fafc', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600' }}
                                onClick={() => showReceiptModal(
                                  (tx.description || '').replace(/\s*[\[\(]Comm:.*?[\]\)]/i, '').trim() || (tx.orderId ? `Order #${tx.orderId}` : 'Transaction'),
                                  (tx.amount || 0) + (tx.commission_GST_Deducted || 0),
                                  tx.commissionDeducted || 0,
                                  tx.gstDeducted || 0,
                                  0, // tcs
                                  0, // platformFee
                                  0, // sms
                                  0, // pgFee
                                  tx.amount || 0,
                                  new Date(tx.createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }),
                                  tx.orderId || '-',
                                  tx.transactionType
                                )}
                              >
                                🧾 View Receipt
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Topup Modal */}
      {showTopupModal && createPortal(
        <div className="modal-overlay fade-in" onClick={() => setShowTopupModal(false)}>
          <div className="modal-content scale-up" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px', padding: 0, borderRadius: '16px', overflow: 'hidden' }}>
            <div className="modal-header" style={{ padding: '24px 24px 20px', margin: 0, borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
              <div className="modal-title-wrap">
                <div className="modal-icon"><ArrowDownRight size={24} color="#14b8a6" /></div>
                <h3 className="modal-title" style={{ margin: 0 }}>Topup Wallet</h3>
              </div>
              <button type="button" onClick={() => setShowTopupModal(false)} className="modal-close" style={{ top: '24px', right: '24px' }}>&times;</button>
            </div>
            <form onSubmit={handleTopupSubmit}>
              <div className="modal-body" style={{ padding: '24px', margin: 0 }}>
                <div className="form-group">
                  <label className="form-label">Amount (₹)</label>
                  <input
                    type="number"
                    className="form-input"
                    style={{ paddingLeft: '16px' }}
                    placeholder="Enter amount to top up (Min ₹100)"
                    value={topupAmount}
                    onChange={(e) => setTopupAmount(e.target.value)}
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '16px' }}>
                  {[100, 500, 1000, 2000].map(amt => (
                    <button
                      key={amt}
                      type="button"
                      onClick={() => setTopupAmount(amt.toString())}
                      style={{
                        padding: '6px 16px',
                        borderRadius: '20px',
                        border: `1px solid ${topupAmount === amt.toString() ? '#14b8a6' : '#cbd5e1'}`,
                        background: topupAmount === amt.toString() ? '#f0fdfa' : '#fff',
                        color: topupAmount === amt.toString() ? '#0f766e' : '#475569',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      ₹{amt}
                    </button>
                  ))}
                </div>
              </div>
              <div className="modal-footer" style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', background: '#fff', margin: 0, display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowTopupModal(false)} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#475569', fontWeight: '600', cursor: 'pointer', fontSize: '14px', transition: 'all 0.2s ease' }}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" style={{ margin: 0, width: 'auto', padding: '10px 24px', borderRadius: '8px', fontSize: '14px', background: '#14b8a6' }}>
                  Proceed to Pay
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
